import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from './keys';
import type { AiModel, AiModelInput, AiProcess, AiProcessPrompt, Budget, Hosting, ModelTestResult, Period, RunGroupList, RunGroupParams, RunList, RunListParams, Usage } from '../types';

export function useHostings() {
  return useQuery({
    queryKey: queryKeys.hostings,
    queryFn: () => api<Hosting[]>('/api/ai/hostings'),
    staleTime: Infinity,
  });
}

export function useModels() {
  return useQuery({ queryKey: queryKeys.models, queryFn: () => api<AiModel[]>('/api/ai/models') });
}

export function useProcesses() {
  return useQuery({
    queryKey: queryKeys.processes,
    queryFn: () => api<AiProcess[]>('/api/ai/processes'),
  });
}

function useInvalidateAi() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ['ai'] });
}

export function useSaveModel() {
  const invalidate = useInvalidateAi();
  return useMutation({
    meta: { success: (model: AiModel) => `${model.display_name} saved` },
    mutationFn: ({ key, body }: { key?: string; body: Partial<AiModelInput> }) =>
      key
        ? api<AiModel>(`/api/ai/models/${key}`, 'PATCH', body)
        : api<AiModel>('/api/ai/models', 'POST', body),
    onSuccess: invalidate,
  });
}

export function useTestModel() {
  const invalidate = useInvalidateAi();
  return useMutation({
    meta: { quiet: true },
    mutationFn: (key: string) => api<ModelTestResult>(`/api/ai/models/${key}/test`, 'POST'),
    onSettled: invalidate,
  });
}

/** Tests the dialog's unsaved values; editing, `key` lends the model's stored keys to blank fields. */
export function useTestDraftModel() {
  const invalidate = useInvalidateAi();
  return useMutation({
    meta: { quiet: true },
    mutationFn: (body: AiModelInput & { key?: string }) =>
      api<ModelTestResult>('/api/ai/models/test', 'POST', body),
    onSettled: invalidate,
  });
}

/** `quiet` leaves the outcome to the caller, which reports a batch of assignments once. */
export function useAssignProcess({ quiet = false }: { quiet?: boolean } = {}) {
  const invalidate = useInvalidateAi();
  return useMutation({
    meta: { success: 'Model assigned', quiet },
    mutationFn: ({ key, model_key }: { key: string; model_key: string }) =>
      api<AiProcess>(`/api/ai/processes/${key}`, 'PATCH', { model_key }),
    onSuccess: invalidate,
  });
}

/** The API's name for a period: a month (YYYY-MM) or a whole year (YYYY). */
function periodParam(period: Period): Record<string, string> {
  return period.length === 4 ? { year: period } : { month: period };
}

export function useUsage(period: Period) {
  return useQuery({
    queryKey: queryKeys.usage(period),
    queryFn: () => api<Usage>(`/api/ai/usage?${new URLSearchParams(periodParam(period))}`),
  });
}

function runQueryString(params: RunListParams | RunGroupParams): string {
  const query = new URLSearchParams({
    ...('by' in params ? { by: params.by } : {}),
    ...periodParam(params.period),
    sort: params.sort,
    order: params.order,
    page: String(params.page),
    page_size: String(params.page_size),
  });
  params.processes.forEach((v) => query.append('process', v));
  params.models.forEach((v) => query.append('model', v));
  params.users.forEach((v) => query.append('user', v));
  params.statuses.forEach((v) => query.append('status', v));
  if (params.created_from) query.set('created_from', params.created_from);
  if (params.created_to) query.set('created_to', params.created_to);
  for (const key of ['cost_min', 'cost_max', 'latency_min_ms', 'latency_max_ms'] as const) {
    const value = params[key];
    if (value !== null) query.set(key, String(value));
  }
  return query.toString();
}

export function useRuns(params: RunListParams) {
  return useQuery({
    queryKey: queryKeys.runs(params),
    queryFn: () => api<RunList>(`/api/ai/runs?${runQueryString(params)}`),
    placeholderData: (previous) => previous,
  });
}

/** The run list's filters, one row per process, model, person or day. */
export function useRunGroups(params: RunGroupParams) {
  return useQuery({
    queryKey: queryKeys.runGroups(params),
    queryFn: () => api<RunGroupList>(`/api/ai/runs/groups?${runQueryString(params)}`),
    placeholderData: (previous) => previous,
  });
}

/** The current month's cap and how much of it is gone. */
export function useBudget() {
  return useQuery({
    queryKey: queryKeys.budget,
    queryFn: () => api<Budget>('/api/ai/budget'),
  });
}

export function useSaveBudget() {
  const invalidate = useInvalidateAi();
  return useMutation({
    meta: { success: 'Budget saved' },
    mutationFn: (body: { limit_usd: string; hard_stop: boolean; alert_threshold_pct: number }) =>
      api<Budget>('/api/ai/budget', 'PUT', body),
    onSuccess: invalidate,
  });
}

/** What an AI process would send now (its feature's Prompt section). */
export function useProcessPrompt(key: string) {
  return useQuery({
    queryKey: queryKeys.processPrompt(key),
    queryFn: () => api<AiProcessPrompt>(`/api/ai/processes/${key}/prompt`),
  });
}
